import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { FilterOperationType, type IFilter } from '@chax-at/prisma-filter-common';
import type { ISchoolSubjectGroupFilter } from 'common/interface/filter.interface';

const baseOrderFilter: IFilter<ISchoolSubjectGroupFilter> = {
  order: [
    { field: 'order', dir: 'asc' },
    { field: 'createdAt', dir: 'asc' },
  ],
};

export async function fetchSchoolSubjectGroupsForClass(params: {
  classId: number;
}): Promise<Components.Schemas.SchoolSubjectGroupDto[]> {
  const res = await Api.Routes.SchoolSubjectGroup.retrieveAllForClass({
    schoolClassId: params.classId,
    $filter: baseOrderFilter,
  });
  return res.rows;
}

export async function fetchTenantSchoolSubjectGroupTemplates(): Promise<Components.Schemas.SchoolSubjectGroupDto[]> {
  const res = await Api.Routes.SchoolSubjectGroup.listTenantTemplates({
    $filter: baseOrderFilter,
  });
  return res.rows;
}

export async function fetchSchoolSubjectGroupsForTemplateSet(params: {
  templateSetId: number;
  schoolLevel: number;
}): Promise<Components.Schemas.SchoolSubjectGroupDto[]> {
  const filter: IFilter<ISchoolSubjectGroupFilter> = {
    ...baseOrderFilter,
    filter: [
      { field: 'schoolLevel', type: FilterOperationType.Eq, value: params.schoolLevel },
      { field: 'templateSetId', type: FilterOperationType.Eq, value: params.templateSetId },
    ],
  };

  const res = await Api.Routes.SchoolSubjectGroup.retrieveGlobalTemplates({ $filter: filter });
  return res.rows;
}

export async function createSchoolSubjectGroup(params: {
  classId: number;
  dto: Components.Schemas.CreateSchoolSubjectGroupDto;
}): Promise<Components.Schemas.SchoolSubjectGroupDto> {
  const res = await Api.Routes.SchoolSubjectGroup.create({ schoolClassId: params.classId }, params.dto);
  return res.data;
}

export async function createTenantSchoolSubjectGroupTemplate(params: {
  dto: Components.Schemas.CreateSchoolSubjectGroupDto;
}): Promise<Components.Schemas.SchoolSubjectGroupDto> {
  const res = await Api.Routes.SchoolSubjectGroup.createTenantTemplate({}, params.dto);
  return res.data;
}

export async function updateSchoolSubjectGroup(params: {
  groupId: number;
  dto: Components.Schemas.UpdateSchoolSubjectGroupDto;
}): Promise<void> {
  await Api.Routes.SchoolSubjectGroup.update({ groupId: params.groupId }, params.dto);
}

export async function deleteSchoolSubjectGroup(params: { groupId: number }): Promise<void> {
  await Api.Routes.SchoolSubjectGroup.delete({ groupId: params.groupId });
}

export async function assignSubjectsToGroup(params: {
  groupId: number;
  dto: Components.Schemas.AssignSubjectDto;
}): Promise<void> {
  await Api.Routes.SchoolSubjectGroup.assignSubjects({ groupId: params.groupId }, params.dto);
}

export async function unassignSubjectsFromGroup(params: {
  groupId: number;
  dto: Components.Schemas.UnassignSubjectDto;
}): Promise<void> {
  await Api.Routes.SchoolSubjectGroup.unassignSubjects({ groupId: params.groupId }, params.dto);
}

export async function reorderUnassignedSubjects(params: {
  dto: Components.Schemas.ReorderUnassignedSubjectDto;
}): Promise<void> {
  await Api.Routes.SchoolSubjectGroup.reorderUnassignedSubjects(null, params.dto);
}

export async function moveSchoolSubjectTemplates(dto: Components.Schemas.MoveSchoolSubjectTemplatesDto): Promise<void> {
  await Api.Routes.SchoolSubjectGroup.moveTemplates({}, dto);
}
